import React, { useState, useEffect, useRef } from 'react';
import { FiZap, FiX, FiCheckCircle, FiMinus, FiMaximize2, FiMessageSquare, FiExternalLink } from 'react-icons/fi';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Gentle two-tone synth chime via Web Audio API (zero external assets needed)
function playDispatchChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    
    // Low tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(587.33, now); // D5
    gain1.gain.setValueAtTime(0.12, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.2);

    // High tone
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(880, now + 0.12); // A5
    gain2.gain.setValueAtTime(0.15, now + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.12);
    osc2.stop(now + 0.45);
  } catch (e) {
    // Ignore audio autoplay restrictions safely
  }
}

export default function TaskDispatchReceiver({ currentUser, onNavigateToAccount }) {
  const [activeDispatch, setActiveDispatch] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const navigatedDispatchIdsRef = useRef(new Set());

  // Unique tab identifier
  const tabIdRef = useRef(`tab_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`);

  // Subscribe to dispatches targeted at the current user
  useEffect(() => {
    if (!currentUser?.email) return;

    const cleanEmail = currentUser.email.toLowerCase().trim();
    // Subscribe to both 'pending' (newly sent) and 'opened' (active, not yet dismissed)
    const q = query(
      collection(db, 'staff_task_dispatches'),
      where('targetEmail', '==', cleanEmail),
      where('status', 'in', ['pending', 'opened'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        setActiveDispatch(null);
        return;
      }

      const docs = [];
      const now = Date.now();
      snapshot.forEach((d) => {
        const data = d.data();
        const createdAtMillis = data.createdAt?.toMillis
          ? data.createdAt.toMillis()
          : data.createdAt?.seconds
          ? data.createdAt.seconds * 1000
          : now;

        // Only consider dispatches created within the last 24 hours to prevent stale cards on initial load
        if (now - createdAtMillis < 24 * 60 * 60 * 1000) {
          docs.push({ id: d.id, ...data, createdAtMillis });
        }
      });

      if (docs.length === 0) {
        setActiveDispatch(null);
        return;
      }

      // Sort newest first
      docs.sort((a, b) => (b.createdAtMillis || 0) - (a.createdAtMillis || 0));
      const latest = docs[0];

      // If this is a brand new 'pending' dispatch:
      if (latest.status === 'pending') {
        const isCurrentTabVisible = typeof document !== 'undefined' && document.visibilityState === 'visible';
        const claimKey = `abacus_dispatch_claimed_${latest.id}`;
        const existingClaim = localStorage.getItem(claimKey);

        // Only navigate and mark opened if this tab is visible or first to claim
        if (isCurrentTabVisible || !existingClaim) {
          localStorage.setItem(claimKey, tabIdRef.current);
          playDispatchChime();

          // Immediately navigate CRM entry to the account
          if (onNavigateToAccount && latest.accountId) {
            onNavigateToAccount(latest.accountId);
            navigatedDispatchIdsRef.current.add(latest.id);
          }

          // Mark status as opened in Firestore so other tabs know it's being handled
          const docRef = doc(db, 'staff_task_dispatches', latest.id);
          updateDoc(docRef, {
            status: 'opened',
            openedAt: serverTimestamp(),
            openedByTab: tabIdRef.current
          }).catch(err => console.warn('[TaskDispatchReceiver] Update error:', err));
        }
      }

      // Keep the active dispatch displayed until closed by the user
      setActiveDispatch(latest);
    }, (err) => {
      console.warn('[TaskDispatchReceiver] Subscription error:', err);
    });

    return () => unsubscribe();
  }, [currentUser, onNavigateToAccount]);

  const handleDismiss = async () => {
    if (!activeDispatch?.id) return;
    const dispatchId = activeDispatch.id;
    setActiveDispatch(null);
    setIsMinimized(false);

    try {
      localStorage.removeItem(`abacus_dispatch_claimed_${dispatchId}`);
      const docRef = doc(db, 'staff_task_dispatches', dispatchId);
      await updateDoc(docRef, {
        status: 'dismissed',
        dismissedAt: serverTimestamp()
      });
    } catch (e) {
      console.warn('Error dismissing dispatch:', e);
    }
  };

  const handleRefocusAccount = () => {
    if (onNavigateToAccount && activeDispatch?.accountId) {
      onNavigateToAccount(activeDispatch.accountId);
    }
  };

  if (!activeDispatch) return null;

  // MINIMIZED FLOATING PILL (Allows user to keep note handy without covering CRM inputs)
  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-[10001] animate-in fade-in slide-in-from-bottom-2 duration-200">
        <div className="bg-white border-2 border-amber-500 rounded-full shadow-xl px-4 py-2.5 flex items-center gap-3 text-[#161a2e]">
          <div className="w-6 h-6 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center font-bold shrink-0">
            <FiZap className="w-3.5 h-3.5" />
          </div>
          <div className="max-w-xs truncate text-xs font-semibold">
            <span className="text-amber-700 font-bold mr-1">Task:</span>
            <span>{activeDispatch.accountName || 'CRM Account'}</span>
          </div>
          <div className="flex items-center gap-1 pl-2 border-l border-[#e6e8ee]">
            <button
              type="button"
              onClick={() => setIsMinimized(false)}
              className="p-1 text-[#575e74] hover:text-amber-700 rounded-lg hover:bg-amber-50 transition cursor-pointer"
              title="Expand task note"
            >
              <FiMaximize2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="p-1 text-[#7b8195] hover:text-rose-600 rounded-lg hover:bg-rose-50 transition cursor-pointer"
              title="Dismiss note"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // EXPANDED FLOATING CARD (Persists until user explicitly clicks Dismiss or Close)
  return (
    <div className="fixed bottom-6 right-6 z-[10001] max-w-md w-full animate-in slide-in-from-bottom-5 duration-300">
      <div className="bg-white border-2 border-amber-500 rounded-2xl shadow-2xl overflow-hidden p-5 text-[#161a2e] relative">
        
        {/* Glow Accent Strip */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-600 animate-pulse" />

        {/* HEADER */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center font-bold shrink-0">
              <FiZap className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-300">
                CRM Task Dispatch
              </span>
              <p className="text-xs text-[#575e74] mt-0.5">
                From <strong className="text-[#161a2e]">{activeDispatch.senderName || 'Management'}</strong>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setIsMinimized(true)}
              className="text-[#7b8195] hover:text-[#161a2e] p-1.5 rounded-lg hover:bg-slate-100 transition cursor-pointer"
              title="Minimize to corner pill"
            >
              <FiMinus className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="text-[#7b8195] hover:text-rose-600 p-1.5 rounded-lg hover:bg-rose-50 transition cursor-pointer"
              title="Close & Dismiss"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* FOCUS TARGET ACCOUNT */}
        <div className="p-3 bg-[#f8f9fb] border border-[#e6e8ee] rounded-xl mb-3">
          <div className="flex items-center justify-between gap-2">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-[#7b8195]">
              Focus Target:
            </span>
            <button
              type="button"
              onClick={handleRefocusAccount}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 hover:text-amber-800 hover:underline cursor-pointer"
              title="Click to view/refocus this account in CRM"
            >
              <span>View Account</span>
              <FiExternalLink className="w-3 h-3" />
            </button>
          </div>
          <h4 className="text-base font-extrabold text-[#161a2e] truncate mt-0.5">
            {activeDispatch.accountName || 'CRM Account'}
          </h4>
          {activeDispatch.accountNumber && (
            <span className="text-[11px] font-mono text-[#7b8195]">
              #{activeDispatch.accountNumber}
            </span>
          )}
        </div>

        {/* BOSS MESSAGE / NOTE CONTAINER (Scrollable if long) */}
        {activeDispatch.note && (
          <div className="mb-4 bg-amber-50/60 border border-amber-200 rounded-xl p-3.5">
            <div className="flex items-center gap-1.5 text-amber-800 text-xs font-bold mb-1">
              <FiMessageSquare className="w-3.5 h-3.5 text-amber-600" />
              <span>Instructions / Note:</span>
            </div>
            <div className="text-xs text-[#161a2e] font-medium whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto pr-1">
              {activeDispatch.note}
            </div>
          </div>
        )}

        {/* FOOTER ACTIONS */}
        <div className="flex items-center justify-between gap-3 pt-1 border-t border-[#f1f2f6]">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
            <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
            <span>Account opened in CRM</span>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="px-4 py-1.5 text-xs font-extrabold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition shadow-sm flex items-center gap-1.5 cursor-pointer"
            title="Click to close this task message"
          >
            <span>Dismiss Note</span>
            <FiX className="w-3.5 h-3.5" />
          </button>
        </div>

      </div>
    </div>
  );
}
